import { useState, useEffect } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Hook para manejar comentarios del lead
 * @param {Object} detallesLead - Detalles del lead actual
 * @param {Function} setDetallesLead - Actualiza detalles del lead
 * @param {Array} contactos - Lista de contactos
 * @param {Function} setContactos - Actualiza contactos
 * @returns {Object} Estados y funciones para gestión de comentarios
 * @returns {Array} return.comentarios - Lista de comentarios
 * @returns {Function} return.setComentarios - Actualiza lista de comentarios
 * @returns {string} return.comentario - Comentario actual en edición
 * @returns {Function} return.setComentario - Actualiza comentario en edición
 * @returns {boolean} return.procesandoComentario - Indica si se está procesando
 * @returns {boolean} return.cargandoComentarios - Indica si se están cargando
 * @returns {number} return.hayMasComentarios - Indica si hay más comentarios
 * @returns {Function} return.GuardarComentario - Guarda un comentario
 * @returns {Function} return.obtenerComentarios - Obtiene comentarios del lead
 * @returns {Function} return.submitComment - Submit del formulario de comentario
 */
export const useLeadComments = (detallesLead, setDetallesLead, contactos, setContactos) => {
    const { token, setToken, nombreCompleto } = useToken();
    const { showErrorToast } = useToast();

    const [comentarios, setComentarios] = useState([]);
    const [comentario, setComentario] = useState('');
    const [procesandoComentario, setProcesandoComentario] = useState(false);
    const [cargandoComentarios, setCargandoComentarios] = useState(false);
    const [hayMasComentarios, setHayMasComentarios] = useState(1);

    // Cargar comentarios cuando cambia el idLead
    useEffect(() => {
        if (detallesLead && detallesLead.idLead && detallesLead.idLead > 0) {
            setCargandoComentarios(true);
            obtenerComentarios(detallesLead.idLead);
        } else {
            setComentarios([]);
            setHayMasComentarios(1);
        }
    }, [detallesLead?.idLead]);

    const GuardarComentario = async (vComentario, iTipoCommentario, _idLead) => {
        setProcesandoComentario(true);

        const response = await fetch("/api/leads/RegistrarComentario?idLead=" + _idLead +
            "&vComentario=" + encodeURIComponent(vComentario) + "&idTipoComentario=" + encodeURIComponent(iTipoCommentario), {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            }
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);

                setProcesandoComentario(false);
                setComentario('');

                if (detallesLead.idLead == ObjDetalles.idLead)
                    setComentarios(prevComentarios => [ObjDetalles, ...prevComentarios]);

                if (detallesLead.idLead != null && (iTipoCommentario === 1 || iTipoCommentario === 3)) {
                    if (contactos !== null && typeof contactos !== 'undefined') {
                        var currentLeadIndex = contactos.findIndex((lead) => lead.idLead === detallesLead.idLead);

                        if (currentLeadIndex == -1) {
                            var detalleslead_Copy = detallesLead;
                            detalleslead_Copy.iDaysDiff = 0;
                            setContactos([detalleslead_Copy, ...contactos]);
                        } else {
                            contactos[currentLeadIndex].iDaysDiff = 0;
                        }
                    }

                    setDetallesLead({
                        ...detallesLead,
                        iDaysDiff: 0
                    });
                }
            } else {
                showErrorToast(data.vMessage);
            }
        } else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }

        setProcesandoComentario(false);
        setComentario('');
    };

    const obtenerComentarios = async (idLead_) => {
        var jDataSend = {
            idLead: idLead_,
            dFechaUltimoComentario: undefined
        };

        const response = await fetch("/api/leads/obtenerComentarios/", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(jDataSend)
        }).catch((error) => {
            console.log('Hubo un problema con la peticion Fetch:' + error);
            setCargandoComentarios(false);
        });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var detalles = JSON.parse(data.detalles);
                setComentarios(detalles.table2);
                setHayMasComentarios(0);
                setCargandoComentarios(false);
            } else {
                console.log("Error: ", data.vMessage);
                setCargandoComentarios(false);
            }
        } else {
            setCargandoComentarios(false);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };

    const submitComment = async (e, lista3LeadsLiberados, setLista3LeadsLiberados, guardarNotificacion) => {
        e.preventDefault();

        if (comentario == '') {
            return;
        }

        setDetallesLead({
            ...detallesLead,
            iDaysDiff: 0
        });

        if (contactos !== null && typeof contactos !== 'undefined') {
            var currentLeadIndex = contactos.findIndex((lead) => lead.idLead === detallesLead.idLead);
            if (currentLeadIndex == -1) {
                var detalleslead_Copy = detallesLead;
                detalleslead_Copy.iDaysDiff = 0;
                setContactos([detalleslead_Copy, ...contactos]);
            } else {
                contactos[currentLeadIndex].iDaysDiff = 0;
            }
        }

        if (lista3LeadsLiberados) {
            setLista3LeadsLiberados(
                lista3LeadsLiberados.map((lead) => {
                    return lead.idLead === detallesLead.idLead
                        ? { ...lead, iDaysDiff: 0 }
                        : lead;
                })
            );
        }

        GuardarComentario(comentario, "1", detallesLead.idLead);

        var objToAction = {
            TypeAction: 1,
            detalles: {
                vUrl: '',
                idModalType: 1,
                data: { idLead: detallesLead.idLead }
            }
        };

        var objToActionString = JSON.stringify(objToAction);
        var jsonData = { idLead: detallesLead.idLead };
        var jsonString = JSON.stringify(jsonData);

        guardarNotificacion("Se hizo un comentario en un lead",
            "<b> " + nombreCompleto + "</b> hizo un comentario en el lead <b>#" + detallesLead.idLead + "</b> : <i> '" + comentario.substring(0, 3) + "...'</i>",
            0,
            objToActionString,
            7,
            jsonString
        );
    };

    return {
        comentarios,
        setComentarios,
        comentario,
        setComentario,
        procesandoComentario,
        cargandoComentarios,
        hayMasComentarios,
        GuardarComentario,
        obtenerComentarios,
        submitComment
    };
};
